import { BigNumber, indexOrDie, singleOrDie } from "@repo/utils";
import type { ConfirmationLine, ScheduleLine } from "./comparison/types.js";
import type { AssistantDecision } from "../../types.js";
import { calculatePositionSplits } from "../../../erp/sap/patch/calculatePositionSplits.js";
import { calculateConfirmedSchedules } from "./calculateConfirmedSchedules.js";

export function makeScheduleLinesAsOrdered(
    scheduleLines: ScheduleLine[],
    persistedConfirmations: ConfirmationLine[],
    reference: string | null,
): ConfirmationLine[] {
    const quantity = scheduleLines.reduce((acc, cur) => {
        return acc.plus(cur.quantity);
    }, new BigNumber(0));

    const lines = singleOrDie(
        calculatePositionSplits(scheduleLines, persistedConfirmations, quantity.toNumber(), false),
    );
    return lines.map(x => {
        if (x.confirmationIdx !== null) {
            return indexOrDie(persistedConfirmations, x.confirmationIdx);
        }

        const scheduleLine = indexOrDie(scheduleLines, x.scheduleIdx);

        return {
            quantity: x.quantity,
            deliveryDate: scheduleLine.deliveryDate,
            reference,
        };
    });
}

export function makeScheduleLinesAsOrderedDecision(
    scheduleLines: ScheduleLine[],
    persistedConfirmations: ConfirmationLine[],
    reference: string | null,
    quantityUom: string | null,
): AssistantDecision {
    const quantity = scheduleLines.reduce((acc, cur) => {
        return acc.plus(cur.quantity);
    }, new BigNumber(0));
    const confirmations = makeScheduleLinesAsOrdered(scheduleLines, persistedConfirmations, reference);
    return {
        type: "accepted",
        origin: "auto",
        value: {
            type: "scheduleLines",
            value: {
                schedules: calculateConfirmedSchedules(scheduleLines, confirmations, quantity).map(line => {
                    return {
                        quantity: {
                            amount: line.datapoints.quantity.nextValue,
                            unit: quantityUom,
                        },
                        deliveryDate: line.datapoints.deliveryDate.nextValue,
                        reference: line.datapoints.reference,
                    };
                }),
                combineMode: "partial",
                confirmations: confirmations.map(line => {
                    return {
                        quantity: {
                            amount: line.quantity,
                            unit: quantityUom,
                        },
                        deliveryDate: line.deliveryDate,
                        reference: line.reference,
                    };
                }),
            },
        },
        approval: {
            state: "no",
            origin: "auto",
        },
    };
}
